import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ThemeToggle } from '../components/ThemeToggle';
import { LanguageSelector } from '../components/LanguageSelector';
import { useI18n } from '../contexts/I18nContext';
import { Mail, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';

export function ForgotPasswordPage() {
  const { tt } = useI18n();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { resetPassword } = useAuth();

  useEffect(() => {
    document.title = tt('Forgot password') + ' - Llambada';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.title = 'Llambada';
      document.head.removeChild(meta);
    };
  }, [tt]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error } = await resetPassword(email);

    setLoading(false);

    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
    }
  };

  return (
    <div className="min-h-screen bg-theme-bg bg-mesh flex flex-col items-center justify-center p-4">
      {/* Theme toggle in top-right corner */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LanguageSelector />
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <img 
            src="/logo.png" 
            alt="Llambada" 
            className="h-16 w-16 mx-auto rounded-2xl shadow-xl shadow-accent-500/25 mb-6"
          />
          <h1 className="font-display text-3xl font-bold text-theme-text mb-2">
            {tt('Reset your password')}
          </h1>
          <p className="text-theme-text-secondary">
            {tt("Enter your email and we'll send you a reset link")}
          </p>
        </div>

        <div className="card p-8 animate-slide-up">
          {success ? (
            <div className="text-center">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-theme-success-bg mb-6">
                <CheckCircle className="h-8 w-8 text-theme-success" />
              </div>
              <h2 className="font-display text-xl font-semibold text-theme-text mb-2">
                {tt('Check your email')}
              </h2>
              <p className="text-theme-text-secondary mb-6">
                {tt("We've sent a password reset link to")} <span className="font-medium text-theme-text">{email}</span>
              </p>
              <p className="text-sm text-theme-text-muted mb-6">
                {tt("Didn't receive the email? Check your spam folder or try again.")}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setSuccess(false);
                    setEmail('');
                  }}
                  className="btn btn-secondary w-full"
                >
                  {tt('Try another email')}
                </button>
                <Link to="/login" className="btn btn-ghost w-full">
                  <ArrowLeft className="h-4 w-4" />
                  {tt('Back to sign in')}
                </Link>
              </div>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-theme-error-bg border border-theme-error-border text-theme-error">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <p className="text-sm">{tt(error)}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-theme-text-secondary mb-2">
                    {tt('Email address')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-theme-text-muted" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input pl-11"
                      placeholder={tt('you@example.com')}
                      required
                      autoFocus
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary w-full py-3"
                >
                  {loading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    tt('Send reset link')
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/login" className="link inline-flex items-center gap-1.5">
                  <ArrowLeft className="h-4 w-4" />
                  {tt('Back to sign in')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
